/**
 * Reportes → Nubecont detallado (`/reportes/nubecont`).
 *
 * Puerto de la ficha «Nubecont detallado» del centro de reportes
 * (`Reportes::nubecont` + `Reportes::get_nubecont_data` + `views/menu/reportes/nubecont.php`).
 *
 * **No lee de esta base**: pide a la **API del proveedor** (Nubecont / nubox360) el listado
 * de comprobantes del rango y lo pinta con las columnas que su sistema contable necesita
 * (el panel anterior las exportaba a Excel con el botón de DataTables).
 *
 * Reglas conservadas:
 * - La llamada es `GET {api_dir_empresa}/documents/lists/{inicio}/{fin}` con
 *   `Authorization: Bearer {token}`, donde la dirección y el token salen de la
 *   configuración de la empresa (`config.api_dir_empresa` y `config.token`, lo mismo que
 *   `Empresa_model::get_registro`); las fechas viajan como `AAAAMMDD` y la espera es de
 *   10 segundos, igual que el `curl` del panel anterior.
 * - Las 26 columnas son las del original, con los mismos valores fijos (`% IGV` 18,
 *   exportación/exonerada/inafecta/ISC/ICBPER a `0`, código de tipo de venta `TV001`,
 *   `Anulado` → `SI` sólo cuando el estado es exactamente «Anulado», DNI → tipo 1 y el
 *   resto tipo 6, `PEN` → `SOLES`).
 * - Las filas se consultan **a petición** (botón *Consultar*), como el original, que
 *   arrancaba la tabla vacía.
 *
 * Diferencias anotadas:
 * - El panel anterior avisaba del fallo con un `alert()` del navegador; aquí se explica
 *   en la propia pantalla, con el motivo (dominio, tiempo de espera, código HTTP), la
 *   dirección configurada y dónde se corrige (Configuración → Empresa).
 * - Se añaden las tarjetas del periodo y el CSV/PDF servidos por la API (el original sólo
 *   exportaba a Excel desde el navegador).
 */

import { AlertCircle, ArrowLeft, Download, FileText, PlugZap, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import { reportesApi, useNubecont, type FilaNubecont, type FiltrosNubecont } from '../reportes.api';

/** Detalle técnico que acompaña al error de la API del proveedor. */
interface DetalleNubecont {
  url?: string | null;
  direccion?: string;
  motivo?: string;
  tipo?: string;
  http_code?: number;
}

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto: el mes en curso (el original pedía las dos fechas a mano). */
function rangoPorDefecto(): FiltrosNubecont {
  const hoy = new Date();

  return {
    desde: `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`,
    hasta: fechaLocal(hoy),
  };
}

/** Importe de una columna del proveedor (llega como número o como texto). */
function importe(valor: number | null): number {
  return typeof valor === 'number' ? valor : Number(valor ?? 0);
}

/** Texto de una celda, con el guion largo del resto del panel cuando viene vacía. */
function celda(valor: string | number | null): string {
  if (valor === null || valor === undefined || valor === '') return '—';

  return String(valor);
}

/** Columnas de la tabla, en el mismo orden del listado del proveedor. */
const COLUMNAS: { titulo: string; clave: (fila: FilaNubecont) => string; ayuda: string }[] = [
  {
    titulo: 'Fecha emisión',
    clave: (fila) => celda(fila.fecha_emision ? formatDateValue(fila.fecha_emision) : null),
    ayuda: 'Fecha de emisión del comprobante (date_of_issue).',
  },
  {
    titulo: 'Fecha venc.',
    clave: (fila) => celda(fila.fecha_vencimiento ? formatDateValue(fila.fecha_vencimiento) : null),
    ayuda: 'Fecha de vencimiento (date_of_due).',
  },
  {
    titulo: 'Tipo doc.',
    clave: (fila) => celda(fila.tipo_documento),
    ayuda: 'Tipo de documento del proveedor (document_type_id: 01 factura, 03 boleta).',
  },
  { titulo: 'Serie', clave: (fila) => celda(fila.serie), ayuda: 'Serie del comprobante.' },
  { titulo: 'Número', clave: (fila) => celda(fila.numero), ayuda: 'Correlativo del comprobante.' },
  {
    titulo: 'Tipo doc. cliente',
    clave: (fila) => fila.tipo_documento_cliente,
    ayuda: '1 para DNI y 6 para RUC, como el panel anterior.',
  },
  {
    titulo: 'Número doc.',
    clave: (fila) => celda(fila.numero_documento_cliente),
    ayuda: 'Documento del cliente (customer_number).',
  },
  {
    titulo: 'Razón social / Apellidos y nombres',
    clave: (fila) => celda(fila.razon_social),
    ayuda: 'Nombre del cliente (customer_name).',
  },
  {
    titulo: 'Moneda',
    clave: (fila) => celda(fila.moneda),
    ayuda: 'PEN se muestra como SOLES, como el original.',
  },
  { titulo: 'T/C', clave: (fila) => celda(fila.tipo_cambio), ayuda: 'Tipo de cambio de venta.' },
  { titulo: '% IGV', clave: (fila) => fila.porcentaje_igv, ayuda: '18 % fijo, como el original.' },
  { titulo: 'Exportación', clave: (fila) => fila.exportacion, ayuda: 'Siempre 0.' },
  {
    titulo: 'Gravada',
    clave: (fila) => formatCurrency(importe(fila.gravada)),
    ayuda: 'Base gravada (total_taxed).',
  },
  { titulo: 'Exonerada', clave: (fila) => fila.exonerada, ayuda: 'Siempre 0.' },
  { titulo: 'Inafecta', clave: (fila) => fila.inafecta, ayuda: 'Siempre 0.' },
  { titulo: 'ISC', clave: (fila) => fila.isc, ayuda: 'Siempre 0.' },
  { titulo: 'IGV', clave: (fila) => formatCurrency(importe(fila.igv)), ayuda: 'IGV del comprobante.' },
  { titulo: 'Otros tributos', clave: () => '—', ayuda: 'El original lo dejaba vacío.' },
  { titulo: 'ICBPER', clave: (fila) => fila.icbper, ayuda: 'Siempre 0.' },
  { titulo: 'Total', clave: (fila) => formatCurrency(importe(fila.total)), ayuda: 'Total del comprobante.' },
  { titulo: 'Detracción?', clave: () => '—', ayuda: 'El original lo dejaba vacío.' },
  {
    titulo: 'Importe detracción',
    clave: () => '—',
    ayuda: 'El original lo dejaba vacío.',
  },
  { titulo: 'Detalle', clave: () => '—', ayuda: 'El original lo dejaba vacío.' },
  {
    titulo: 'Anulado?',
    clave: (fila) => celda(fila.anulado),
    ayuda: 'SI cuando el estado del comprobante es «Anulado».',
  },
  { titulo: 'Consolidado?', clave: () => '—', ayuda: 'El original lo dejaba vacío.' },
  {
    titulo: 'Código del tipo de venta',
    clave: (fila) => fila.codigo_tipo_venta,
    ayuda: 'TV001 fijo, como el original.',
  },
];

export function ReporteNubecontPage() {
  useDocumentTitle('Reportes · Nubecont detallado');

  const [filtros, setFiltros] = useState<FiltrosNubecont>(rangoPorDefecto);
  const [consultado, setConsultado] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const consulta = useNubecont(filtros, consultado);
  const datos = consulta.data;
  const resumen = datos?.resumen;
  const filas = datos?.filas ?? [];
  // El proveedor puede devolver miles de comprobantes: se pagina en el navegador y el pie
  // sigue sumando el listado completo.
  const paginacion = usePaginacion(filas, { clave: filtros });

  const fallo = consulta.error instanceof ApiError ? consulta.error : null;
  const detalle = (fallo?.details as { detalle?: DetalleNubecont } | undefined)?.detalle;

  const cambiar = <C extends keyof FiltrosNubecont>(campo: C, valor: FiltrosNubecont[C]) =>
    setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const consultar = () => {
    setAviso(null);

    if (consultado) {
      void consulta.refetch();

      return;
    }

    setConsultado(true);
  };

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarNubecont(filtros), {
        nombre: `nubecont-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (falloDescarga) {
      setAviso(
        falloDescarga instanceof Error ? falloDescarga.message : 'No se pudo exportar el listado.',
      );
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfNubecont(filtros));
    } catch (falloDescarga) {
      setAviso(
        falloDescarga instanceof Error ? falloDescarga.message : 'No se pudo generar el PDF.',
      );
    }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Nubecont detallado</h1>
          <p className="mt-1 text-sm text-ink-500">
            Los comprobantes del sistema contable del proveedor (Nubecont) en el mismo formato
            de su exportación: fecha de emisión y vencimiento, tipo, serie y número, cliente,
            base, IGV y total.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => void consultar()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
            disabled={consulta.isFetching}
          >
            {consulta.isFetching ? 'Consultando…' : 'Actualizar'}
          </Button>

          <Button
            variant="outline"
            onClick={() => void exportar()}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
            disabled={!datos}
          >
            Exportar
          </Button>

          <Button
            variant="outline"
            onClick={() => void exportarPdf()}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
            disabled={!datos}
          >
            PDF
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <div className="flex flex-col gap-1.5 text-sm xl:col-span-2">
              <span className="font-medium text-ink-700">&nbsp;</span>
              <Button
                onClick={() => void consultar()}
                leftIcon={<PlugZap className="size-4" aria-hidden="true" />}
                disabled={consulta.isFetching}
              >
                {consulta.isFetching ? 'Consultando la API…' : 'Consultar'}
              </Button>
            </div>
          </div>

          <p className="border-t border-ink-100 pt-3 text-[11px] leading-relaxed text-ink-500">
            El listado no sale de esta base: se pide a la API del proveedor con la dirección y
            el token de <strong className="font-semibold">Configuración → Empresa</strong>. Las
            fechas viajan como <code>AAAAMMDD</code> y la espera es de 10 segundos, igual que en
            el panel anterior.
          </p>
        </CardContent>
      </Card>

      {fallo ? (
        <Alert variant="error" title="No se pudo consultar la API de Nubecont">
          <p>{fallo.message}</p>

          {detalle?.direccion ? (
            <p className="mt-1 break-all font-mono text-[11px] opacity-80">
              {detalle.direccion}
            </p>
          ) : null}

          {detalle?.tipo === 'dominio' || detalle?.tipo === 'rechazada' ? (
            <p className="mt-1 text-[12px]">
              Revisa la <strong className="font-semibold">Dirección API</strong> y el{' '}
              <strong className="font-semibold">Token API</strong> en{' '}
              <Link to="/configuracion/empresa" className="font-semibold underline">
                Configuración → Empresa
              </Link>
              ; mientras la API del proveedor no responda, este reporte no puede mostrar datos.
            </p>
          ) : null}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo completar la operación">
          {aviso}
        </Alert>
      ) : null}

      {!consultado ? (
        <Alert variant="info" title="Consulta bajo demanda">
          Pulsa <strong className="font-semibold">Consultar</strong> para pedir el listado del
          rango elegido. Como cada consulta viaja a la API del proveedor, no se repite sola:
          con <strong className="font-semibold">Actualizar</strong> vuelves a pedir el mismo
          rango.
        </Alert>
      ) : null}

      {consulta.isLoading ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((indice) => (
            <Skeleton key={indice} className="h-24 rounded-card" />
          ))}
        </div>
      ) : null}

      {datos ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            {
              etiqueta: 'Comprobantes',
              valor: formatNumber(resumen?.comprobantes ?? 0),
              detalle: `${formatNumber(resumen?.clientes ?? 0)} cliente(s)`,
            },
            {
              etiqueta: 'Base gravada',
              valor: formatCurrency(resumen?.gravada ?? 0),
              detalle: 'total gravado (total_taxed)',
            },
            {
              etiqueta: 'IGV',
              valor: formatCurrency(resumen?.igv ?? 0),
              detalle: 'IGV del periodo',
            },
            {
              etiqueta: 'Total',
              valor: formatCurrency(resumen?.total ?? 0),
              detalle: `${formatNumber(resumen?.anulados ?? 0)} anulado(s)`,
            },
          ].map((tarjeta) => (
            <Card key={tarjeta.etiqueta}>
              <CardContent className="p-4">
                <p className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                  {tarjeta.etiqueta}
                </p>
                <p className="mt-1 text-xl font-bold text-ink-900">{tarjeta.valor}</p>
                <p className="mt-0.5 text-xs text-ink-500">{tarjeta.detalle}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      {datos ? (
        <Card>
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-100 px-4 py-3">
              <p className="text-sm font-semibold text-ink-800">
                Comprobantes del proveedor ({formatNumber(filas.length)})
              </p>
              <div className="flex flex-wrap items-center gap-2 text-[11px] text-ink-500">
                <Badge variant="neutral" className="px-1.5 py-0 text-[10px]">
                  {datos.filtros.inicio} → {datos.filtros.fin}
                </Badge>
                <span className="break-all font-mono">{datos.origen.url}</span>
              </div>
            </div>

            <div className="max-h-[65vh] overflow-auto">
              <table className="w-full min-w-[1500px] border-collapse text-left text-[11px]">
                <thead className="sticky top-0 z-10 bg-ink-50 text-[10px] tracking-wide text-ink-600 uppercase">
                  <tr>
                    {COLUMNAS.map((columna) => (
                      <th
                        key={columna.titulo}
                        scope="col"
                        title={columna.ayuda}
                        className="border-b border-ink-100 px-2 py-2 font-semibold whitespace-nowrap"
                      >
                        {columna.titulo}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filas.length === 0 ? (
                    <tr>
                      <td
                        colSpan={COLUMNAS.length}
                        className="px-4 py-8 text-center text-sm text-ink-500"
                      >
                        La API respondió sin comprobantes en ese rango.
                      </td>
                    </tr>
                  ) : (
                    paginacion.visibles.map((fila, indice) => (
                      <tr
                        key={`${fila.serie ?? ''}-${fila.numero ?? ''}-${indice}`}
                        className={
                          fila.anulado === 'SI'
                            ? 'border-t border-ink-100 bg-rose-50/40'
                            : 'border-t border-ink-100 hover:bg-brand-50/30'
                        }
                      >
                        {COLUMNAS.map((columna) => (
                          <td
                            key={columna.titulo}
                            className="px-2 py-1.5 whitespace-nowrap text-ink-700"
                          >
                            {columna.clave(fila)}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
                {filas.length > 0 ? (
                  <tfoot className="bg-ink-50 text-[11px] font-semibold text-ink-800">
                    <tr>
                      <td className="px-2 py-2" colSpan={12}>
                        TOTAL · {formatNumber(filas.length)} comprobante(s) ·{' '}
                        {formatNumber(resumen?.anulados ?? 0)} anulado(s)
                      </td>
                      <td className="px-2 py-2">
                        {formatCurrency(importe(resumen?.gravada ?? 0))}
                      </td>
                      <td className="px-2 py-2" colSpan={3} />
                      <td className="px-2 py-2">{formatCurrency(importe(resumen?.igv ?? 0))}</td>
                      <td className="px-2 py-2" colSpan={2} />
                      <td className="px-2 py-2">{formatCurrency(importe(resumen?.total ?? 0))}</td>
                      <td className="px-2 py-2" colSpan={6} />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="comprobantes" />
          </CardContent>
        </Card>
      ) : null}

      <Alert variant="info" title="Sobre este reporte">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong className="font-semibold">No es un reporte de esta base</strong>: es el mismo
            servicio que usa el sistema contable del proveedor. Si su API no responde, aquí se
            ve el motivo (dominio inexistente, conexión rechazada, tiempo de espera o código
            HTTP) y no un error genérico.
          </li>
          <li>
            Las <strong className="font-semibold">26 columnas</strong> son las de la exportación
            del panel anterior (que sólo tenía botón de Excel); aquí se añaden el CSV y el PDF
            de la API.
          </li>
          <li>
            El anulado se marca con <strong className="font-semibold">SI</strong> sólo cuando el
            estado del comprobante es exactamente «Anulado», y las columnas que el original
            dejaba vacías (otros tributos, detracción, detalle y consolidado) se muestran con
            «—».
          </li>
          <li>
            Si el proveedor responde pero sin comprobantes en el rango, la tabla lo dice: la
            consulta se hizo bien.
          </li>
        </ul>

        <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] opacity-80">
          <AlertCircle className="size-3.5" aria-hidden="true" />
          El detalle técnico de la última consulta aparece arriba cuando falla.
        </p>
      </Alert>
    </div>
  );
}
